import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Bookmark, BookmarkCheck, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { setAfterAuth } from "@/components/CouponOfDay";
import { loadSavedIds, onSavedChange, toggleSaved, rememberSignupCta } from "@/lib/membership";

// "Salva" su articoli e card: riservato agli iscritti, per gli altri è un invito a registrarsi.
export function SaveButton({ articleId, testid = "save-article-button", compact = false }) {
  const { user } = useAuth();
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);

  useEffect(() => {
    if (!user) return undefined;
    loadSavedIds().then((ids) => setSaved(ids.has(articleId))).catch(() => {});
    return onSavedChange((ids) => setSaved(!!ids?.has(articleId)));
  }, [user, articleId]);

  const click = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // dentro una card non deve aprire l'articolo
    if (!user) { setAsk(true); return; }
    setBusy(true);
    try {
      const on = await toggleSaved(articleId);
      toast.success(on ? "Salvato in «Salvati per dopo»" : "Tolto dai salvati");
    } catch (err) { toast.error(err.message || "Operazione non riuscita"); } finally { setBusy(false); }
  };

  const cls = compact
    ? `w-8 h-8 inline-flex items-center justify-center rounded-full transition-colors disabled:opacity-60 ${saved ? "text-brand bg-brand/10" : "text-subtle hover:text-brand hover:bg-brand/10"}`
    : `inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-sm font-semibold transition-colors disabled:opacity-60 ${saved ? "border-brand bg-brand/10 text-brand" : "border-hair text-onSurfaceSecondary hover:border-brand/50 hover:text-brand"}`;
  const Icon = saved ? BookmarkCheck : Bookmark;
  return (
    <>
      <button type="button" onClick={click} disabled={busy} data-testid={testid} aria-pressed={saved}
        aria-label={saved ? "Togli dai salvati" : "Salva per dopo"} title={saved ? "Salvato" : "Salva per dopo"} className={cls}>
        <Icon size={compact ? 15 : 16} /> {!compact && (saved ? "Salvato" : "Salva")}
      </button>
      {ask && createPortal(<SaveInvite onClose={() => setAsk(false)} />, document.body)}
    </>
  );
}

function SaveInvite({ onClose }) {
  const go = () => { rememberSignupCta("salva"); setAfterAuth(window.location.pathname); };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60" data-testid="save-invite-modal"
      onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md bg-surfaceSecondary border border-hair rounded-2xl p-6">
        <div className="flex items-start justify-between gap-3">
          <span className="w-11 h-11 rounded-full bg-brand/15 text-brand flex items-center justify-center"><Bookmark size={20} /></span>
          <button type="button" onClick={onClose} data-testid="save-invite-close" aria-label="Chiudi" className="w-8 h-8 flex items-center justify-center rounded-full border border-hair text-subtle hover:text-brand"><X size={16} /></button>
        </div>
        <p data-testid="save-invite-text" className="mt-4 font-serif text-xl font-semibold leading-snug text-onSurface">
          Registrati gratuitamente per salvare questa notizia e ritrovarla quando vuoi.
        </p>
        <Link to="/registrati" onClick={go} data-testid="save-invite-register"
          className="mt-5 w-full inline-flex items-center justify-center px-6 py-3.5 rounded-full bg-brand text-onBrand text-sm font-bold uppercase tracking-wide hover:bg-brandSecondary transition-colors">
          Registrati gratis
        </Link>
        <p className="mt-3 text-center text-xs text-subtle">Hai già un account? <Link to="/login" onClick={go} data-testid="save-invite-login" className="text-brand font-semibold hover:underline">Accedi</Link></p>
      </div>
    </div>
  );
}
